'use client';

import { useCallback, useState, useRef } from 'react';
import type {
  UploadedFile,
  UploadError,
  UploadUrlResponse,
  FileValidationError,
  InitialFile,
  FileUploadStatus,
} from '../types';
import {
  DEFAULT_MAX_RETRIES,
  DEFAULT_RETRY_DELAY_MS,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_ACCEPTED_TYPES,
  classifyError,
  getRetryDelay,
  createTimeoutPromise,
  sleep,
  createXhrUpload,
} from '../utils';

export type UseFileUploadStateOptions = {
  getUploadUrl: (file: File) => Promise<UploadUrlResponse>;
  accept?: string;
  maxFileSize?: number;
  multiple?: boolean;
  maxRetries?: number;
  retryDelayMs?: number;
  timeoutMs?: number;
  disabled?: boolean;
  initialFiles?: InitialFile[];
  onUploadComplete?: (fileUrl: string, file: File) => void;
  onUploadError?: (error: Error, file: File) => void;
  onFileRemove?: (fileId?: string) => void;
  onRetry?: (file: File, retryCount: number, error: UploadError) => void;
  onValidationError?: (error: FileValidationError) => string | undefined;
};

export type UseFileUploadStateReturn = {
  files: UploadedFile[];
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFiles: (fileList: FileList | null) => void;
  handleRemoveFile: (index: number) => void;
  handleRetryFile: (index: number) => void;
  handleDragEnter: (e: React.DragEvent) => void;
  handleDragLeave: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/**
 * Custom hook for FileUpload state and logic
 */
export const useUploadState = (options: UseFileUploadStateOptions): UseFileUploadStateReturn => {
  const {
    getUploadUrl,
    accept = DEFAULT_ACCEPTED_TYPES,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    multiple = false,
    maxRetries = DEFAULT_MAX_RETRIES,
    retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    disabled = false,
    initialFiles,
    onUploadComplete,
    onUploadError,
    onFileRemove,
    onRetry,
    onValidationError,
  } = options;

  // Convert initialFiles to UploadedFile format for display
  // Use empty array fallback to handle undefined from Storybook controls
  const initialUploadedFiles: UploadedFile[] = (initialFiles ?? []).map((file) => ({
    file: new File([], file.fileName, { type: file.mimeType }),
    status: 'success' as FileUploadStatus,
    progress: 100,
    url: file.fileId,
    isPreExisting: true,
  }));

  const [files, setFiles] = useState<UploadedFile[]>(initialUploadedFiles);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  // ========================================
  // File State Management
  // ========================================

  const updateFileState = useCallback((index: number, updates: Partial<UploadedFile>) => {
    setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
  }, []);

  // ========================================
  // Upload Logic
  // ========================================

  const uploadFile = useCallback(
    async (file: File, index: number, retryCount: number = 0) => {
      const abortController = new AbortController();
      abortControllersRef.current.set(index, abortController);

      try {
        // Update status
        updateFileState(index, {
          status: retryCount > 0 ? 'retrying' : 'uploading',
          progress: 0,
          retryCount,
          error:
            retryCount > 0
              ? `Retrying... (attempt ${retryCount + 1}/${maxRetries + 1})`
              : undefined,
        });

        // Get signed URL with timeout
        let uploadUrl: string;
        let fileUrl: string;
        let tenantId: string | undefined;

        try {
          const urlResponse = await Promise.race([
            getUploadUrl(file),
            createTimeoutPromise(timeoutMs),
          ]);
          uploadUrl = urlResponse.uploadUrl;
          fileUrl = urlResponse.fileUrl;
          tenantId = urlResponse.tenantId;
        } catch (urlError) {
          const classifiedError = classifyError(urlError);
          throw Object.assign(new Error(classifiedError.message), {
            uploadError: classifiedError,
          });
        }

        // Upload to S3
        await createXhrUpload({
          uploadUrl,
          file,
          timeoutMs,
          onProgress: (progress) => updateFileState(index, { progress }),
          abortSignal: abortController.signal,
          tenantId,
        });

        // Success
        updateFileState(index, {
          status: 'success',
          progress: 100,
          url: fileUrl,
          error: undefined,
          retryCount: 0,
        });

        abortControllersRef.current.delete(index);
        onUploadComplete?.(fileUrl, file);
      } catch (error) {
        abortControllersRef.current.delete(index);

        // Handle abort
        if (error instanceof Error && error.message === 'Upload aborted') {
          updateFileState(index, { status: 'error', error: 'Upload cancelled' });
          return;
        }

        // Classify error
        const uploadError =
          (error as Error & { uploadError?: UploadError }).uploadError ||
          classifyError(error, (error as Error & { statusCode?: number }).statusCode);

        // Retry if possible
        if (uploadError.retryable && retryCount < maxRetries) {
          const delay = getRetryDelay(retryCount, retryDelayMs);
          onRetry?.(file, retryCount + 1, uploadError);

          updateFileState(index, {
            status: 'retrying',
            error: `${uploadError.message} Retrying in ${Math.round(delay / 1000)}s...`,
            retryCount: retryCount + 1,
          });

          await sleep(delay);
          return uploadFile(file, index, retryCount + 1);
        }

        // Final error
        const finalMessage =
          retryCount >= maxRetries
            ? `${uploadError.message} (max retries exceeded)`
            : uploadError.message;

        updateFileState(index, {
          status: 'error',
          error: finalMessage,
          retryCount,
          isValidationError: false,
        });
        onUploadError?.(new Error(finalMessage), file);
      }
    },
    [
      getUploadUrl,
      maxRetries,
      onRetry,
      onUploadComplete,
      onUploadError,
      retryDelayMs,
      timeoutMs,
      updateFileState,
    ]
  );

  // ========================================
  // File Handling
  // ========================================

  const getValidationErrorMessage = useCallback(
    (file: File): string | undefined => {
      // Check file size
      if (file.size > maxFileSize) {
        const customMessage = onValidationError?.({
          type: 'FILE_TOO_LARGE',
          file,
          maxFileSize,
        });
        return (
          customMessage ?? `File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`
        );
      }

      // Check file type
      const acceptedTypesList = accept.split(',').map((t) => t.trim().toLowerCase());
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

      if (!acceptedTypesList.includes(fileExtension)) {
        const customMessage = onValidationError?.({
          type: 'INVALID_FILE_TYPE',
          file,
          acceptedTypes: accept,
        });
        return customMessage ?? `File type ${fileExtension} is not supported`;
      }

      return undefined;
    },
    [accept, maxFileSize, onValidationError]
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || disabled) return;

      const newFiles: UploadedFile[] = Array.from(fileList).map((file) => {
        const validationError = getValidationErrorMessage(file);
        return {
          file,
          status: validationError ? ('error' as const) : ('idle' as const),
          progress: 0,
          error: validationError,
          isValidationError: !!validationError,
        };
      });

      setFiles((prev) => {
        // Prepend new files so they appear at the top of the list
        const updated = multiple ? [...newFiles, ...prev] : newFiles;
        return updated;
      });

      // Start uploads - new files are now at the beginning
      newFiles.forEach((f, i) => {
        if (f.status === 'idle') {
          uploadFile(f.file, i);
        }
      });
    },
    [disabled, getValidationErrorMessage, multiple, uploadFile]
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      // Abort ongoing upload
      const abortController = abortControllersRef.current.get(index);
      if (abortController) {
        abortController.abort();
        abortControllersRef.current.delete(index);
      }

      // Capture fileId before removing from state
      let removedFileId: string | undefined;
      setFiles((prev) => {
        removedFileId = prev[index]?.url;
        return prev.filter((_, i) => i !== index);
      });

      // Notify parent after state update to avoid setState-during-render
      queueMicrotask(() => {
        onFileRemove?.(removedFileId);
      });
    },
    [onFileRemove]
  );

  const handleRetryFile = useCallback(
    (index: number) => {
      const fileToRetry = files[index];
      if (fileToRetry?.status === 'error') {
        // Re-validate file before retrying upload
        const validationError = getValidationErrorMessage(fileToRetry.file);
        if (validationError) {
          // Update with fresh validation error (in case validation rules changed)
          updateFileState(index, {
            status: 'error',
            error: validationError,
            isValidationError: true,
          });
          return;
        }

        // Clear validation error flag and proceed with upload
        updateFileState(index, { isValidationError: false });
        uploadFile(fileToRetry.file, index, 0);
      }
    },
    [files, getValidationErrorMessage, updateFileState, uploadFile]
  );

  // ========================================
  // Drag & Drop Handlers
  // ========================================

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) setIsDragging(true);
    },
    [disabled]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!disabled) handleFiles(e.dataTransfer.files);
    },
    [disabled, handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFiles]
  );

  return {
    files,
    isDragging,
    fileInputRef,
    handleFiles,
    handleRemoveFile,
    handleRetryFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleInputChange,
  };
};
